import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Tooltip, IconButton, ClickAwayListener } from "@material-ui/core";
import {
  Add,
  ChevronLeft,
  ChevronRight,
  FiberManualRecord,
  Check,
  Edit,
  Delete,
} from "@material-ui/icons";
import Button from "components/CustomButtons/Button";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";
import ctsGuidePortalStyle from "assets/jss/material-dashboard-pro-react/views/ctsGuidePortalStyle";
import useThemeColors from "hooks/useThemeColors";
import { ThreeDots } from "react-loader-spinner";

const useStyles = makeStyles(ctsGuidePortalStyle);

// Status configuration
const STATUS_CONFIG = {
  not_started: { color: "#9e9e9e", label: "not_started" },
  on_track: { color: "#4caf50", label: "on_track" },
  at_risk: { color: "#ff9800", label: "at_risk" },
  delayed: { color: "#f44336", label: "delayed" },
  completed: { color: "#2196f3", label: "completed" },
};

const STATUS_ORDER = [
  "not_started",
  "on_track",
  "at_risk",
  "delayed",
  "completed",
];

// Status Picker Component
function StatusPicker({ value, onChange }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (status) => {
    onChange(status);
    setIsOpen(false);
  };

  const getStatusClassName = (statusValue) => {
    const classMap = {
      not_started: "statusDotNotStarted",
      on_track: "statusDotOnTrack",
      at_risk: "statusDotAtRisk",
      delayed: "statusDotDelayed",
      completed: "statusDotCompleted",
    };
    return classes[classMap[statusValue]] || "";
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div className={classes.statusPickerContainer}>
        <Tooltip
          title={t(
            `ctsguide.build_status.${STATUS_CONFIG[value]?.label || "not_started"}`,
          )}
        >
          <div
            className={`${classes.statusDot} ${getStatusClassName(value)}`}
            onClick={() => setIsOpen(!isOpen)}
            style={{ backgroundColor: STATUS_CONFIG[value]?.color }}
          >
            {value === "completed" && (
              <Check style={{ fontSize: "12px", color: "#fff" }} />
            )}
          </div>
        </Tooltip>
        {isOpen && (
          <div className={classes.statusPickerDropdown}>
            {STATUS_ORDER.map((status) => (
              <Tooltip
                key={status}
                title={t(`ctsguide.build_status.${status}`)}
              >
                <div
                  className={`${classes.statusDot} ${value === status ? classes.statusDotActive : ""}`}
                  style={{ backgroundColor: STATUS_CONFIG[status].color }}
                  onClick={() => handleSelect(status)}
                >
                  {status === "completed" && (
                    <Check style={{ fontSize: "12px", color: "#fff" }} />
                  )}
                </div>
              </Tooltip>
            ))}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}

StatusPicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

// Key Message Editable Input
function KeyMessageInput({
  value,
  onChange,
  placeholder,
  isExpanded,
  onToggle,
}) {
  const classes = useStyles();
  const [localValue, setLocalValue] = useState(value || "");
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      textareaRef.current?.blur();
    }
    if (e.key === "Escape") {
      setLocalValue(value || "");
      setIsEditing(false);
    }
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  // Editing mode - show textarea
  if (isEditing) {
    return (
      <textarea
        ref={textareaRef}
        className={classes.keyMessageTextarea}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={3}
      />
    );
  }

  // Display mode
  return (
    <div
      className={`${classes.keyMessageDisplay} ${isExpanded ? classes.keyMessageDisplayExpanded : ""}`}
      onDoubleClick={handleDoubleClick}
      onClick={onToggle}
    >
      {localValue || (
        <span className={classes.keyMessagePlaceholder}>{placeholder}</span>
      )}
    </div>
  );
}

KeyMessageInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default function BuildStatus() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [creating, setCreating] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const currentSection = sections[currentSectionIndex];

  const toggleRowExpanded = useCallback((rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await client.get("/api/ctsguide/build-status-data/");
      setSections(res.data.sections || []);
      // Find current section index
      const currentIdx = (res.data.sections || []).findIndex(
        (s) => s.is_current,
      );
      setCurrentSectionIndex(currentIdx >= 0 ? currentIdx : 0);
    } catch (error) {
      console.error("Error loading build status data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSection = async () => {
    setCreating(true);
    try {
      await client.post("/api/ctsguide/build-sections/");
      await loadData();
      setCurrentSectionIndex(0); // New section is always first
    } catch (error) {
      console.error("Error creating section:", error);
    } finally {
      setCreating(false);
    }
  };

  const handlePreviousSection = useCallback(() => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  }, [currentSectionIndex, sections.length]);

  const handleNextSection = useCallback(() => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  }, [currentSectionIndex]);

  const handleDeleteSection = useCallback(async () => {
    if (!currentSection) return;
    if (!window.confirm(t("ctsguide.build_status.confirm_delete_section"))) {
      return;
    }
    try {
      await client.delete(`/api/ctsguide/build-sections/${currentSection.id}/`);
      await loadData();
    } catch (error) {
      console.error("Error deleting section:", error);
    }
  }, [currentSection, t]);

  const handleEditSection = useCallback(() => {
    if (!currentSection) return;
    const newStartDate = window.prompt(
      t("ctsguide.build_status.edit_start_date"),
      currentSection.start_date,
    );
    if (!newStartDate) return;
    const newEndDate = window.prompt(
      t("ctsguide.build_status.edit_end_date"),
      currentSection.end_date,
    );
    if (!newEndDate) return;

    client
      .put(`/api/ctsguide/build-sections/${currentSection.id}/`, {
        start_date: newStartDate,
        end_date: newEndDate,
      })
      .then(() => loadData())
      .catch((error) => console.error("Error updating section:", error));
  }, [currentSection, t]);

  const handleStatusChange = useCallback(
    async (statusId, newStatus) => {
      try {
        await client.put(`/api/ctsguide/build-line-status/${statusId}/`, {
          status: newStatus,
        });
        // Update local state
        setSections((prev) =>
          prev.map((section, idx) => {
            if (idx === currentSectionIndex) {
              return {
                ...section,
                line_statuses: section.line_statuses.map((ls) =>
                  ls.id === statusId ? { ...ls, status: newStatus } : ls,
                ),
              };
            }
            return section;
          }),
        );
      } catch (error) {
        console.error("Error updating status:", error);
      }
    },
    [currentSectionIndex],
  );

  const handleKeyMessageChange = useCallback(
    async (statusId, keyMessage) => {
      try {
        await client.put(`/api/ctsguide/build-line-status/${statusId}/`, {
          key_message: keyMessage,
        });
        // Update local state
        setSections((prev) =>
          prev.map((section, idx) => {
            if (idx === currentSectionIndex) {
              return {
                ...section,
                line_statuses: section.line_statuses.map((ls) =>
                  ls.id === statusId ? { ...ls, key_message: keyMessage } : ls,
                ),
              };
            }
            return section;
          }),
        );
      } catch (error) {
        console.error("Error updating key message:", error);
      }
    },
    [currentSectionIndex],
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Sort line statuses by pl_line_order
  const sortedLineStatuses = currentSection?.line_statuses
    ? [...currentSection.line_statuses].sort(
        (a, b) => a.pl_line_order - b.pl_line_order,
      )
    : [];

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <ThreeDots width="120px" color={clientColor} ariaLabel="loading" />
      </div>
    );
  }

  // Empty state - no sections yet
  if (sections.length === 0) {
    return (
      <Box>
        <p style={{ color: "#666", fontSize: "0.9em", marginBottom: "20px" }}>
          {t("ctsguide.build_status.description")}
        </p>

        <div className={classes.buildStatusEmpty}>
          <FiberManualRecord className={classes.buildStatusEmptyIcon} />
          <p className={classes.buildStatusEmptyText}>
            {t("ctsguide.build_status.no_sections")}
          </p>
          <Button
            color="client"
            onClick={handleCreateSection}
            disabled={creating}
          >
            {creating ? (
              <ThreeDots height="20" width="40" color="#fff" />
            ) : (
              <>
                <Add style={{ marginRight: "8px" }} />
                {t("ctsguide.build_status.start_first_section")}
              </>
            )}
          </Button>
        </div>
      </Box>
    );
  }

  return (
    <Box>
      {/* Section Navigation */}
      <div className={classes.sectionNavContainer}>
        <IconButton
          className={`${classes.sectionNavArrow} ${
            currentSectionIndex >= sections.length - 1
              ? classes.sectionNavArrowDisabled
              : ""
          }`}
          onClick={handlePreviousSection}
          disabled={currentSectionIndex >= sections.length - 1}
        >
          <ChevronLeft />
        </IconButton>

        <div className={classes.sectionInfo}>
          <span className={classes.sectionDates}>
            {formatDate(currentSection?.start_date)} →{" "}
            {formatDate(currentSection?.end_date)}
          </span>
          <span className={classes.sectionLabel}>
            {currentSection?.is_current
              ? t("ctsguide.build_status.current_section")
              : `#${sections.length - currentSectionIndex}`}
          </span>
          <Tooltip title={t("ctsguide.build_status.edit_section")}>
            <IconButton
              size="small"
              className={classes.sectionActionBtn}
              onClick={handleEditSection}
            >
              <Edit style={{ fontSize: "14px" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("ctsguide.build_status.delete_section")}>
            <IconButton
              size="small"
              className={classes.sectionActionBtn}
              onClick={handleDeleteSection}
            >
              <Delete style={{ fontSize: "14px" }} />
            </IconButton>
          </Tooltip>
        </div>

        <Box display="flex" alignItems="center" gap={1}>
          {currentSectionIndex === 0 && (
            <Tooltip title={t("ctsguide.build_status.new_section")}>
              <IconButton
                style={{ color: clientColor }}
                onClick={handleCreateSection}
                disabled={creating}
              >
                {creating ? (
                  <ThreeDots height="16" width="16" color={clientColor} />
                ) : (
                  <Add />
                )}
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            className={`${classes.sectionNavArrow} ${
              currentSectionIndex <= 0 ? classes.sectionNavArrowDisabled : ""
            }`}
            onClick={handleNextSection}
            disabled={currentSectionIndex <= 0}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </div>

      {/* Build Status Table */}
      <table className={classes.buildStatusTable}>
        <colgroup>
          <col style={{ width: "160px" }} />
          <col style={{ width: "80px" }} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className={classes.buildStatusHeaderCell}>
              {t("ctsguide.build_status.pl_line")}
            </th>
            <th
              className={classes.buildStatusHeaderCell}
              style={{ textAlign: "center" }}
            >
              {t("ctsguide.build_status.status")}
            </th>
            <th className={classes.buildStatusHeaderCell}>
              {t("ctsguide.build_status.key_message")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedLineStatuses.map((lineStatus) => {
            const levelClass =
              lineStatus.pl_line_level === 1
                ? classes.buildStatusRowLevel1
                : lineStatus.pl_line_level === 2
                  ? classes.buildStatusRowLevel2
                  : classes.buildStatusRowLevel3;

            return (
              <tr
                key={lineStatus.id}
                className={`${classes.buildStatusRow} ${levelClass}`}
              >
                <td
                  className={`${classes.buildStatusCell} ${classes.buildStatusLineCell}`}
                >
                  {lineStatus.pl_line_name}
                </td>
                <td
                  className={`${classes.buildStatusCell} ${classes.buildStatusStatusCell}`}
                >
                  <StatusPicker
                    value={lineStatus.status}
                    onChange={(newStatus) =>
                      handleStatusChange(lineStatus.id, newStatus)
                    }
                  />
                </td>
                <td
                  className={`${classes.buildStatusCell} ${classes.buildStatusMessageCell}`}
                >
                  <KeyMessageInput
                    value={lineStatus.key_message}
                    onChange={(msg) =>
                      handleKeyMessageChange(lineStatus.id, msg)
                    }
                    placeholder={t(
                      "ctsguide.build_status.key_message_placeholder",
                    )}
                    isExpanded={expandedRows[lineStatus.id]}
                    onToggle={() => toggleRowExpanded(lineStatus.id)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Status Legend - below the table */}
      <div className={classes.statusLegend}>
        {STATUS_ORDER.map((status) => (
          <div key={status} className={classes.statusLegendItem}>
            <div
              className={classes.statusLegendDot}
              style={{ backgroundColor: STATUS_CONFIG[status].color }}
            />
            <span>{t(`ctsguide.build_status.${status}`)}</span>
          </div>
        ))}
      </div>
    </Box>
  );
}
